/* Overplayed Rotation — dependency-free Shopify Online Store components. */
(() => {
  'use strict';
  if (window.OverplayedRotation) return;
  const configNode = document.getElementById('overplayed-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const root = `${config.root.replace(/\/$/, '')}/`;
  const text = (key, values = {}) => Object.entries(values).reduce(
    (value, [name, replacement]) => value.split(`{${name}}`).join(String(replacement)),
    config.text[key] || ''
  );
  const money = amount => new Intl.NumberFormat(config.locale || 'en', {
    style: 'currency', currency: config.currency
  }).format(amount / 100);
  const el = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = content;
    return node;
  };
  const qs = (node, selector) => node.querySelector(selector);
  const amountValid = value => Number.isSafeInteger(value) && value > 0;
  const totals = { 1: config.anchor, ...config.prices };
  const discounts = [0, 0, ...[2, 3].map(n => config.anchor * n - totals[n])];
  const offerValid = (config.offersLive || config.previewOnly) && config.currency === text('currency').trim().toUpperCase()
    && amountValid(config.anchor) && [2, 3].every(n => amountValid(totals[n])
      && totals[n] > totals[n - 1] && discounts[n] >= discounts[n - 1]);
  const discountAt = count => offerValid ? discounts[Math.min(3, Math.max(0, count))] : 0;
  let state = null;
  let refreshing = null;
  let refreshAgain = false;
  let timer;
  let mutating = false;
  let uncertain = false;
  const listeners = new Set();
  const subscribers = callback => { listeners.add(callback); if (state) callback(state); return () => listeners.delete(callback); };
  const eligibleItems = snapshot => {
    const ids = new Set(snapshot.eligibleProductIds.map(String));
    return snapshot.cart.items.filter(item => ids.has(String(item.product_id)) && !item.selling_plan_allocation);
  };
  const eligibleCount = snapshot => eligibleItems(snapshot).reduce((sum, item) => sum + item.quantity, 0);
  const compatible = snapshot => config.previewOnly || eligibleItems(snapshot).every(item => item.original_price === config.anchor);
  const quote = (count, snapshot = state) => {
    const existing = snapshot ? eligibleCount(snapshot) : 0;
    return config.anchor * count - (discountAt(existing + count) - discountAt(existing));
  };
  const rotationTotal = count => config.anchor * count - discountAt(count);
  const cartSignature = snapshot => eligibleItems(snapshot).map(item => `${item.key}:${item.quantity}:${item.variant_id}`).sort().join('|');
  const cartPicks = snapshot => eligibleItems(snapshot).flatMap(item => Array.from({ length: item.quantity }, () => ({
    product: {
      id: String(item.product_id), handle: item.handle, title: item.product_title || item.title,
      image: (typeof item.featured_image === 'string' ? item.featured_image : item.featured_image?.url) || item.image || null, available: true
    },
    variant: {
      id: item.variant_id, title: item.variant_title || 'Default Title', price: item.original_price,
      available: true, requires_selling_plan: false
    },
    cartRef: { key: item.key, variantId: String(item.variant_id) }
  })));
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) {
        let message = 'The shop could not complete this request.';
        try { const body = await response.json(); message = body.description || body.message || message; } catch (_) { /* Non-JSON platform error. */ }
        throw new Error(typeof message === 'string' ? message : 'Please review your cart and try again.');
      }
      return response;
    } finally { clearTimeout(timeout); }
  }
  async function sectionData(name, selector, source) {
    const url = source ? new URL(source, location.origin) : new URL(root, location.origin);
    if (url.origin !== location.origin) throw new Error('The shop returned an invalid page link.');
    url.searchParams.set('section_id', name);
    const response = await request(url);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = page.querySelector(selector);
    if (!node) throw new Error('The shop could not load this section. Please refresh the page.');
    return JSON.parse(node.textContent);
  }
  async function refreshCart() {
    if (refreshing) { refreshAgain = true; return refreshing; }
    refreshing = (async () => {
      do {
        refreshAgain = false;
        state = await sectionData('overplayed-cart-state', '[data-op-cart-state]');
        if (!state.cart || !Array.isArray(state.cart.items) || !Array.isArray(state.eligibleProductIds)) throw new Error('Cart data is unavailable.');
        listeners.forEach(callback => callback(state));
      } while (refreshAgain);
      return state;
    })();
    try { return await refreshing; } finally { refreshing = null; }
  }
  const scheduleRefresh = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (mutating) { scheduleRefresh(); return; }
      refreshCart().catch(() => { /* Preserve last good state; writes always fetch fresh state. */ });
    }, 200);
  };
  // Observe completion of native theme Ajax mutations without replacing fetch or XHR.
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(list => {
        if (list.getEntries().some(entry => /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(new URL(entry.name).pathname))) scheduleRefresh();
      }).observe({ type: 'resource', buffered: false });
    } catch (_) { /* Event integration and full-page navigation remain supported. */ }
  }
  ['cart:updated', 'cart:refresh', 'cart:change', 'overplayed:cart-changed', 'shopify:section:load'].forEach(name => document.addEventListener(name, scheduleRefresh));
  window.addEventListener('pageshow', scheduleRefresh);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleRefresh(); });

  const productCache = new Map();
  async function productData(handle, fresh = false) {
    if (fresh) productCache.delete(handle);
    if (!productCache.has(handle)) {
      productCache.set(handle, request(`${root}products/${encodeURIComponent(handle)}.js`)
        .then(response => response.json()).catch(error => { productCache.delete(handle); throw error; }));
    }
    return productCache.get(handle);
  }
  const suitable = product => product.available && !product.requiresSellingPlan
    && product.variantCount <= 250 && (config.previewOnly || (product.priceMin === config.anchor && product.priceMax === config.anchor));
  const sizeSupported = variant => !/(?:^|\s*\/\s*)(?:[3-9]X(?:L|Large)?|XXXL|XXXXL|XXXXXL)(?:$|\s*\/\s*)/i.test(String(variant.title || '').trim());
  const variantSuitable = variant => sizeSupported(variant) && variant.available && !variant.requires_selling_plan
    && (config.previewOnly || variant.price === config.anchor) && (!variant.quantity_rule
      || (variant.quantity_rule.min <= 1 && variant.quantity_rule.increment <= 1));

  const artists = String(config.artists || '').split('\n').map(line => {
    const [label, handle] = line.split('|').map(value => value.trim());
    return { label, handle };
  }).filter(artist => artist.label && artist.handle);

  // Share the complete eligible catalog across drawer instances. The first page
  // alone may not contain any designs by the artist whose ad was clicked.
  let recommendationCatalog;
  function loadRecommendationCatalog() {
    if (!recommendationCatalog) recommendationCatalog = (async () => {
      const products = new Map();
      const visited = new Set();
      let next = null;
      do {
        const data = await sectionData('overplayed-catalog', '[data-op-catalog]', next);
        for (const product of data.products || []) products.set(product.handle, product);
        next = data.next;
        if (next && visited.has(next)) throw new Error('Catalog pagination repeated.');
        if (next) visited.add(next);
      } while (next);
      return [...products.values()].filter(suitable);
    })().catch(error => { recommendationCatalog = null; throw error; });
    return recommendationCatalog;
  }
  function recommendationArtists(products, snapshot) {
    const known = new Set(artists.map(artist => artist.handle));
    const forProduct = product => (product?.collections || []).filter(handle => known.has(handle));
    const seedNode = document.querySelector('op-rotation [data-op-seed]');
    let landing = [];
    try {
      landing = seedNode ? forProduct(JSON.parse(seedNode.textContent)) : JSON.parse(sessionStorage.getItem('op-landing-artists') || '[]');
      if (seedNode) sessionStorage.setItem('op-landing-artists', JSON.stringify(landing));
    } catch (_) { /* Storage may be disabled; cart context remains available. */ }
    const byHandle = new Map(products.map(product => [product.handle, product]));
    const inCart = snapshot ? eligibleItems(snapshot).flatMap(item => forProduct(byHandle.get(item.handle))) : [];
    return [...new Set([...landing, ...inCart])].filter(handle => known.has(handle));
  }
  function rankRecommendations(products, snapshot) {
    const preferred = recommendationArtists(products, snapshot);
    const campaign = ['juice-wrld', 'kanye'].filter(handle => !preferred.includes(handle));
    const existing = new Set(snapshot ? eligibleItems(snapshot).map(item => item.handle) : []);
    const remaining = products.filter(product => !existing.has(product.handle));
    const result = [], used = new Set();
    // Round-robin each priority group so mixed carts and the two ad artists
    // both receive representation, without repeating designs.
    for (const group of [preferred, campaign]) {
      const queues = group.map(handle => remaining.filter(product => (product.collections || []).includes(handle)));
      while (queues.some(queue => queue.length)) for (const queue of queues) {
        const product = queue.shift();
        if (product && !used.has(product.handle)) { result.push(product); used.add(product.handle); }
      }
    }
    return result.concat(remaining.filter(product => !used.has(product.handle)));
  }

  // Refresh Shopify-rendered drawer markup after a confirmed write. A failed
  // drawer refresh must never make a successful cart addition look unsuccessful.
  async function presentCart(snapshot) {
    try {
      const oldDrawer = document.querySelector('theme-drawer#cart-drawer, cart-drawer-component');
      if (!oldDrawer) { window.location.assign(config.cartUrl); return; }
      const wrapper = oldDrawer.closest('[id^="shopify-section-"]');
      const sectionId = wrapper?.id.replace('shopify-section-', '');
      if (!sectionId) throw new Error('Drawer section is unavailable.');
      const url = new URL(location.href);
      url.searchParams.set('section_id', sectionId);
      const response = await request(url);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const freshDrawer = page.querySelector('theme-drawer#cart-drawer, cart-drawer-component');
      if (!freshDrawer || !freshDrawer.querySelector('dialog')) throw new Error('Drawer markup is unavailable.');
      document.querySelectorAll('op-bump, op-rotation').forEach(host => host.picker?.close());
      const openDialog = oldDrawer.querySelector('dialog[open]');
      if (openDialog) openDialog.close();
      const newDrawer = document.importNode(freshDrawer, true);
      oldDrawer.replaceWith(newDrawer);
      const oldIcons = [...document.querySelectorAll('cart-icon')];
      const freshIcons = [...page.querySelectorAll('cart-icon')];
      if (oldIcons.length === freshIcons.length) oldIcons.forEach((icon, index) => icon.replaceWith(document.importNode(freshIcons[index], true)));
      else oldIcons.forEach(icon => { if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(snapshot.cart.item_count); });
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (typeof newDrawer.open === 'function') newDrawer.open();
      else if (typeof newDrawer.showDialog === 'function') newDrawer.showDialog();
      else newDrawer.querySelector('dialog').showModal();
      document.dispatchEvent(new CustomEvent('overplayed:cart-changed', { detail: { cart: snapshot.cart } }));
    } catch (_) {
      // Preserve a usable, server-rendered cart if this theme changes its API.
      window.location.assign(config.cartUrl);
    }
  }
  const shippingAvailable = snapshot => config.shippingEnabled && (config.shippingLive || config.previewOnly)
    && amountValid(config.shippingThreshold) && snapshot.cart.currency === text('currency').trim().toUpperCase()
    && text('shipping_countries').toUpperCase().split(',').map(value => value.trim()).includes(config.country);
  function sizeChart(mount) {
    if (!mount || mount.dataset.opChartReady) return;
    mount.dataset.opChartReady = 'true';
    const rows = String(config.sizeRows || '').split('\n').map(line => line.split(',').map(value => value.trim()))
      .filter(row => row.length === 3 && ['S','M','L','XL','2XL'].includes(row[0]) && Number(row[1]) > 0 && Number(row[2]) > 0);
    if (!rows.length || (!config.sizeVerified && !config.previewOnly)) { mount.hidden = true; return; }
    const details = el('details', 'op-size-chart');
    const summary = el('summary', '', 'Size chart');
    const content = el('div', 'op-size-chart__body');
    const units = el('div', 'op-units'); units.setAttribute('role', 'group'); units.setAttribute('aria-label', 'Measurement units');
    const table = el('table');
    const inch = el('button', '', 'Inches'), cm = el('button', '', 'CM');
    inch.type = cm.type = 'button';
    function render(unit) {
      inch.setAttribute('aria-pressed', String(unit === 'in')); cm.setAttribute('aria-pressed', String(unit === 'cm'));
      const head = el('thead'), headings = el('tr');
      ['Size', `Chest width (${unit})`, `Length (${unit})`].forEach(label => { const cell = el('th','',label); cell.scope='col'; headings.append(cell); });
      head.append(headings); const body = el('tbody');
      rows.forEach(row => { const tr=el('tr'); const label=el('th','',row[0]); label.scope='row'; tr.append(label);
        row.slice(1).forEach(value => tr.append(el('td','',unit === 'in' ? (Number(value)/2.54).toFixed(2) : (Math.round(Number(value)*100)/100).toFixed(2)))); body.append(tr); });
      table.replaceChildren(head,body);
    }
    inch.addEventListener('click',()=>render('in')); cm.addEventListener('click',()=>render('cm'));
    units.append(inch,cm); content.append(units,table,el('p','op-help','Comfort Colors® 1717. Measured flat, pit to pit. Allow ±1.5 in (3.81 cm) variation.'));
    if (!config.sizeVerified) content.append(el('p','op-help','Draft measurements — confirm before launch.'));
    details.append(summary,content); mount.replaceChildren(details); render('in');
  }
  function syncProductChart() {
    document.querySelectorAll('[id*="ovp_accordions"] details').forEach(details => {
      if (qs(details,'summary')?.textContent.trim().toLowerCase().includes('size chart')) {
        const mount=qs(details,'.body');
        if (mount && !mount.dataset.opChartReady && config.sizeVerified) {
          sizeChart(mount);
          // Retain the product page's existing accordion; use only the shared chart body.
          const content=qs(mount,'.op-size-chart__body'); if(content)mount.replaceChildren(content);
          mount.classList.add('op');
        }
      }
    });
  }

  class Picker {
    constructor(host, onChoose) {
      this.host = host;
      this.dialog = qs(host, '[data-op-dialog]');
      this.onChoose = onChoose;
      this.products = [];
      this.next = null;
      this.loaded = false;
      this.loading = false;
      this.version = 0;
      this.abort = new AbortController();
      const on = (selector, event, handler) => qs(host, selector).addEventListener(event, handler, { signal: this.abort.signal });
      const artistSelect = qs(host, '[data-op-artist]');
      artists.forEach(artist => { const option=el('option','',artist.label); option.value=artist.handle; artistSelect.append(option); });
      on('[data-op-artist]', 'change', () => { this.renderProducts(); this.searchMore(); });
      sizeChart(qs(host,'[data-op-size-chart]'));
      on('[data-op-close]', 'click', () => this.close());
      this.dialog.addEventListener('close', () => this.cleanup(), { signal: this.abort.signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) {
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      } }, { signal: this.abort.signal });
      on('[data-op-back]', 'click', () => this.browse());
      on('[data-op-more]', 'click', () => this.load());
      on('[data-op-search]', 'input', () => {
        this.renderProducts();
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.searchMore(), 300);
      });
      on('[data-op-confirm]', 'click', () => {
        if (this.variant && variantSuitable(this.variant)) this.onChoose({ product: this.product, variant: this.variant });
      });
    }
    async open(index, total, seed, trigger) {
      this.index = index;
      this.total = total;
      this.returnFocus = trigger || document.activeElement;
      qs(this.host, '[data-op-dialog-step]').textContent = `TEE ${index + 1} OF ${total}`;
      qs(this.host, '[data-op-dialog-error]').textContent = '';
      if (!this.dialog.open) { this.dialog.showModal(); document.documentElement.classList.add('op-modal-open'); }
      if (seed) await this.selectProduct(seed);
      else await this.browse();
    }
    close() { this.dialog.close(); }
    cleanup() {
      this.version++;
      if (!document.querySelector('.op-dialog[open]')) document.documentElement.classList.remove('op-modal-open');
      this.returnFocus?.focus({ preventScroll: true });
    }
    destroy() { if (this.dialog.open) this.close(); clearTimeout(this.searchTimer); this.abort.abort(); }
    async searchMore() {
      const query = qs(this.host, '[data-op-search]').value.trim();
      if ((!query && !qs(this.host,'[data-op-artist]').value) || !this.dialog.open || qs(this.host, '[data-op-browse]').hidden) return;
      if (this.loading) { this.searchTimer = setTimeout(() => this.searchMore(), 300); return; }
      if (this.next) {
        const previousNext = this.next;
        await this.load();
        if (this.next && this.next !== previousNext) this.searchTimer = setTimeout(() => this.searchMore(), 50);
      }
    }
    async browse() {
      this.version++;
      this.variant = null;
      qs(this.host, '[data-op-browse]').hidden = false;
      qs(this.host, '[data-op-variant-panel]').hidden = true;
      qs(this.host, '[data-op-confirm-wrap]').hidden = true;
      qs(this.host, '[data-op-dialog-title]').textContent = text('choose_design');
      qs(this.host, '[data-op-dialog-error]').textContent = '';
      if (!this.loaded) await this.load();
      else this.renderProducts();
      this.searchMore();
      // Keep the mobile keyboard closed until the shopper chooses to search.
      if (this.dialog.open) qs(this.host, '[data-op-dialog-title]').focus({ preventScroll: true });
    }
    async load() {
      if (this.loading) return;
      this.loading = true;
      const more = qs(this.host, '[data-op-more]');
      more.disabled = true;
      qs(this.host, '[data-op-catalog-message]').textContent = 'Loading designs…';
      try {
        const data = await sectionData('overplayed-catalog', '[data-op-catalog]', this.loaded ? this.next : null);
        const ids = new Set(this.products.map(product => product.id));
        for (const product of data.products) if (suitable(product) && !ids.has(product.id)) { this.products.push(product); ids.add(product.id); }
        this.next = data.next;
        this.loaded = true;
        this.renderProducts();
      } catch (error) {
        qs(this.host, '[data-op-catalog-message]').textContent = error.message;
        more.hidden = false;
        more.textContent = 'Retry loading designs';
      } finally { this.loading = false; more.disabled = false; }
    }
    renderProducts() {
      const query = qs(this.host, '[data-op-search]').value.trim().toLocaleLowerCase();
      const artist = qs(this.host, '[data-op-artist]').value;
      const filtering = Boolean(query || artist);
      const products = this.products.filter(product => product.title.toLocaleLowerCase().includes(query)
        && (!artist || (product.collections || []).includes(artist)));
      const fragment = document.createDocumentFragment();
      for (const product of products) {
        const button = el('button', 'op-product');
        button.type = 'button';
        button.setAttribute('aria-label', `Choose ${product.title}`);
        if (product.image) { const image = el('img'); image.src = product.image; image.alt = ''; image.width = 300; image.height = 350; image.loading = 'lazy'; button.append(image); }
        else button.append(el('span', 'op-image-placeholder', 'TEE'));
        button.append(el('strong', '', product.title), el('span', 'op-product__action', 'Choose design →'));
        button.addEventListener('click', () => this.selectProduct(product));
        const card = el('div', 'op-product-card'); card.append(button, productLink(product, this.host, this.index));
        fragment.append(card);
      }
      qs(this.host, '[data-op-products]').replaceChildren(fragment);
      const more = qs(this.host, '[data-op-more]');
      more.hidden = !this.next;
      more.textContent = 'Load more designs';
      qs(this.host, '[data-op-catalog-message]').textContent = products.length ? `${products.length} designs${this.next ? (filtering ? ' · Loading matches…' : '') : ''}`
        : this.next ? (filtering ? 'Loading matches…' : 'More designs available.') : 'No matching tees. Try another artist or search.';
    }
    async selectProduct(product) {
      const version = ++this.version;
      this.product = product;
      this.variant = null;
      qs(this.host, '[data-op-browse]').hidden = true;
      qs(this.host, '[data-op-variant-panel]').hidden = false;
      qs(this.host, '[data-op-confirm-wrap]').hidden = false;
      qs(this.host, '[data-op-confirm]').disabled = true;
      qs(this.host, '[data-op-dialog-title]').textContent = text('choose_size');
      qs(this.host, '[data-op-chosen-title]').textContent = product.title;
      qs(this.host, '[data-op-variant-panel] > .op-product-link')?.remove();
      qs(this.host, '.op-chosen').after(productLink(product, this.host, this.index));
      const image = qs(this.host, '[data-op-chosen-image]');
      image.hidden = !product.image;
      if (product.image) image.src = product.image;
      qs(this.host, '[data-op-variants]').replaceChildren();
      qs(this.host, '[data-op-variant-help]').textContent = 'Loading available sizes…';
      qs(this.host, '[data-op-dialog-error]').textContent = '';
      try {
        const data = await productData(product.handle);
        if (version !== this.version || !this.dialog.open) return;
        if (data.requires_selling_plan || data.variants.length > 250) throw new Error('This tee is unavailable in the rotation. Please choose another design.');
        const fragment = document.createDocumentFragment();
        for (const variant of data.variants.filter(sizeSupported)) {
          const button = el('button', 'op-size', variant.title === 'Default Title' ? 'One size' : variant.title);
          button.type = 'button';
          button.disabled = !variantSuitable(variant);
          button.setAttribute('aria-pressed', 'false');
          if (button.disabled) button.setAttribute('aria-label', `${button.textContent} — unavailable`);
          button.addEventListener('click', () => {
            this.variant = variant;
            qs(this.host, '[data-op-variants]').querySelectorAll('button').forEach(node => node.setAttribute('aria-pressed', String(node === button)));
            qs(this.host, '[data-op-confirm]').disabled = false;
            if (variant.featured_image?.src) { image.src = variant.featured_image.src; image.hidden = false; }
          });
          fragment.append(button);
        }
        qs(this.host, '[data-op-variants]').replaceChildren(fragment);
        const hasAvailable = data.variants.some(variantSuitable);
        qs(this.host, '[data-op-variant-help]').textContent = hasAvailable ? '' : 'This design is currently unavailable. Choose another tee.';
        qs(this.host, '[data-op-variants] button:not(:disabled)')?.focus({ preventScroll: true });
      } catch (error) { if (version === this.version) qs(this.host, '[data-op-dialog-error]').textContent = error.message; }
    }
  }

  async function addSelection(picks, expectedQuote, bump = false) {
    if (config.previewOnly) throw new Error('This draft previews the proposed offer. Checkout will be enabled after the store pricing is configured.');
    if (mutating) throw new Error('Your cart is being updated. Please wait.');
    if (uncertain) throw new Error('Please review your cart before adding again.');
    mutating = true;
    let submitted = false;
    try {
      const before = await refreshCart();
      if (!offerValid || !compatible(before)) throw new Error('Rotation pricing is unavailable. Please refresh and use the standard product options.');
      if (bump && (eligibleCount(before) < 1 || eligibleCount(before) >= 3)) throw new Error('Your cart changed. Refresh to see the next available rotation reward.');
      const expected = quote(picks.length, before);
      if (expected !== expectedQuote) throw new Error('Your cart changed. Review the updated price, then add your tees.');
      // Fresh product data catches stale sizes/prices; Shopify still owns inventory validation.
      const handles = [...new Set(picks.map(pick => pick.product.handle))];
      const fresh = new Map(await Promise.all(handles.map(async handle => [handle, await productData(handle, true)])));
      for (const pick of picks) {
        const product = fresh.get(pick.product.handle);
        const variant = product.variants.find(item => String(item.id) === String(pick.variant.id));
        if (!variant || !variantSuitable(variant) || product.requires_selling_plan) throw new Error(`${pick.product.title}: this size or offer changed. Please choose it again.`);
      }
      const attempt = window.crypto?.randomUUID ? crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const grouped = new Map();
      picks.forEach(pick => {
        const id = String(pick.variant.id);
        if (grouped.has(id)) grouped.get(id).quantity++;
        else grouped.set(id, { id, quantity: 1, properties: { _op_attempt: attempt } });
      });
      submitted = true;
      await request(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [...grouped.values()] })
      });
      const after = await refreshCart();
      const added = after.cart.items.filter(item => item.properties?._op_attempt === attempt);
      const addedCount = added.reduce((sum, item) => sum + item.quantity, 0);
      const expectedCount = eligibleCount(before) + picks.length;
      if (addedCount !== picks.length || eligibleCount(after) !== expectedCount
        || [...grouped].some(([id, requested]) => added.filter(item => String(item.variant_id) === id).reduce((n, item) => n + item.quantity, 0) !== requested.quantity)) {
        throw new Error('Your cart changed while adding. Review the items in your cart before continuing.');
      }
      const eligible = eligibleItems(after);
      const actual = eligible.reduce((sum, item) => sum + item.final_line_price, 0);
      const expectedEligibleTotal = config.anchor * expectedCount - discountAt(expectedCount);
      if (!compatible(after) || actual > expectedEligibleTotal) {
        throw new Error('Your tees were added, but the rotation discount could not be confirmed. Please review the cart total before checkout.');
      }
      document.dispatchEvent(new CustomEvent('overplayed:added', { detail: { cart: after.cart, quantity: picks.length, bump } }));
      await presentCart(after);
    } catch (error) {
      if (submitted) { uncertain = true; error.message += ' Do not add again until you have checked your cart.'; scheduleRefresh(); }
      throw error;
    } finally { mutating = false; }
  }

  async function saveRotation(rotation) {
    if (config.previewOnly) throw new Error('This draft previews the proposed offer. Checkout will be enabled after the store pricing is configured.');
    if (mutating) throw new Error('Your cart is being updated. Please wait.');
    if (uncertain) throw new Error('Please review your cart before making another change.');
    mutating = true;
    let submitted = false;
    try {
      const before = await refreshCart();
      if (cartSignature(before) !== rotation.cartSignature) throw new Error('Your cart changed. Refresh the page to load your current rotation.');
      const picks = rotation.picks.slice(0, rotation.count);
      const additions = picks.filter(pick => !pick.cartRef || String(pick.variant.id) !== pick.cartRef.variantId);
      // Retain only unchanged units. Cleared/replaced slots and a smaller tier
      // remove their original cart units when the customer explicitly saves.
      const retained = new Map();
      picks.filter(pick => pick.cartRef && String(pick.variant.id) === pick.cartRef.variantId)
        .forEach(pick => retained.set(pick.cartRef.key, (retained.get(pick.cartRef.key) || 0) + 1));
      const updates = {};
      eligibleItems(before).forEach(item => {
        const quantity = retained.get(item.key) || 0;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      });
      if (!additions.length && !Object.keys(updates).length) return before;
      const handles = [...new Set(additions.map(pick => pick.product.handle))];
      const fresh = new Map(await Promise.all(handles.map(async handle => [handle, await productData(handle, true)])));
      additions.forEach(pick => {
        const product = fresh.get(pick.product.handle);
        const variant = product?.variants.find(item => String(item.id) === String(pick.variant.id));
        if (!variant || !variantSuitable(variant) || product.requires_selling_plan) throw new Error(`${pick.product.title}: that size is no longer available.`);
      });
      const attempt = window.crypto?.randomUUID ? crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const grouped = new Map();
      additions.forEach(pick => {
        const id = String(pick.variant.id);
        if (grouped.has(id)) grouped.get(id).quantity++;
        else grouped.set(id, { id, quantity: 1, properties: { _op_attempt: attempt } });
      });
      submitted = true;
      if (additions.length) await request(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [...grouped.values()] })
      });
      if (Object.keys(updates).length) {
        await request(`${root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates })
        });
      }
      const after = await refreshCart();
      const expectedCount = picks.length;
      const actualTotal = eligibleItems(after).reduce((sum, item) => sum + item.final_line_price, 0);
      if (eligibleCount(after) !== expectedCount || actualTotal > rotationTotal(expectedCount)) {
        throw new Error('The rotation changed, but its final price could not be confirmed. Review your cart before checkout.');
      }
      clearDraft();
      rotation.draftLoaded = true;
      rotation.dirty = false;
      rotation.hydrate(after);
      rotation.render();
      await presentCart(after);
      return after;
    } catch (error) {
      if (submitted) { uncertain = true; error.message += ' Review your cart before trying again.'; scheduleRefresh(); }
      throw error;
    } finally { mutating = false; }
  }


  // Draft selections are separate from the Shopify cart. Only explicit Add/Save
  // writes to Shopify; a cart signature prevents stale drafts restoring removed items.
  const draftKey = 'op-rotation-draft-v2:' + config.currency + ':' + config.collection;
  function readDraft(snapshot) {
    try {
      const draft = JSON.parse(localStorage.getItem(draftKey) || 'null');
      if (!draft || Date.now() - draft.savedAt > 86400000 || draft.signature !== cartSignature(snapshot)
        || ![1,2,3].includes(draft.count) || !Array.isArray(draft.picks) || draft.picks.length > 3) return null;
      if (draft.picks.some(pick => pick && (!pick.product || !/^[a-z0-9][a-z0-9-]*$/i.test(pick.product.handle)
        || typeof pick.product.title !== 'string' || (pick.variant && !sizeSupported(pick.variant))))) return null;
      return draft;
    } catch (_) { return null; }
  }
  function storeDraft(rotation) {
    try {
      localStorage.setItem(draftKey, JSON.stringify({savedAt: Date.now(), signature: rotation.cartSignature,
        count: rotation.count, picks: Array.from({length: rotation.count}, (_, i) => rotation.picks[i] || null)}));
    } catch (_) { /* Private browsing/storage limits must not break buying. */ }
  }
  function clearDraft() { try { localStorage.removeItem(draftKey); } catch (_) {} }
  function productLink(product, host, slot) {
    const link = el('a', 'op-product-link', 'View full page');
    const url = new URL(root + 'products/' + encodeURIComponent(product.handle), location.origin);
    if (host instanceof Rotation && Number.isInteger(slot)) url.searchParams.set('op_slot', String(slot + 1));
    link.href = url.href;
    link.setAttribute('aria-label', 'View full page for ' + product.title);
    link.addEventListener('click', () => { if (host instanceof Rotation) storeDraft(host); });
    return link;
  }

  class Rotation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.picks = [];
      this.draftLoaded = false;
      this.count = config.defaultTwo ? 2 : 1;
      this.busy = false;
      this.abort = new AbortController();
      const seed = qs(this, '[data-op-seed]');
      this.seed = seed ? JSON.parse(seed.textContent) : null;
      this.picker = new Picker(this, pick => {
        const cartRef = this.picks[this.picker.index]?.cartRef;
        this.picks[this.picker.index] = { ...pick, ...(cartRef ? { cartRef } : {}) };
        this.dirty = true;
        storeDraft(this);
        this.render();
        const next = this.picks.slice(0, this.count).findIndex(item => !item?.variant);
        const missing = next >= 0 ? next : this.picks.length < this.count ? this.picks.length : -1;
        this.picker.close();
        (missing >= 0 ? qs(this, `[data-op-slot="${missing}"]`) : qs(this, '[data-op-submit]'))?.focus({ preventScroll: true });
      });
      this.querySelectorAll('.op-tier input').forEach(input => input.addEventListener('change', () => {
        if (Number(input.value) < this.count) this.picks = this.picks.filter(Boolean);
        this.count = Number(input.value);
        this.picks = this.picks.slice(0, this.count);
        this.dirty = true;
        storeDraft(this);
        this.render();
      }, { signal: this.abort.signal }));
      qs(this, '[data-op-submit]').addEventListener('click', () => this.submit(), { signal: this.abort.signal });
      this.unsubscribe = subscribers(snapshot => {
        if (this.dirty && this.cartSignature !== cartSignature(snapshot) && !mutating) {
          clearDraft(); this.dirty = false; this.draftLoaded = true;
          qs(this, '[data-op-error]').textContent = 'Your cart changed. Your rotation now matches your cart.';
        }
        if (!this.dirty) this.hydrate(snapshot);
        this.render();
      });
      window.addEventListener('pageshow', event => {
        if (event.persisted && !this.busy) { this.dirty = false; this.draftLoaded = false; scheduleRefresh(); }
      }, {signal: this.abort.signal});
      window.addEventListener('storage', event => {
        if (event.key === draftKey && !this.busy && state) {
          this.dirty = false; this.draftLoaded = false; scheduleRefresh();
        }
      }, {signal: this.abort.signal});
      refreshCart().then(() => this.render()).catch(() => { /* Native fallback stays visible. */ });
    }
    disconnectedCallback() {
      this.unsubscribe?.(); this.abort?.abort(); this.picker?.destroy(); this.initialized = false;
    }
    hydrate(snapshot) {
      if (!this.draftLoaded) {
        this.draftLoaded = true;
        const draft = readDraft(snapshot);
        if (draft) {
          this.count = draft.count; this.picks = draft.picks;
          this.cartCount = eligibleCount(snapshot); this.cartSignature = draft.signature; this.dirty = true;
          this.querySelectorAll('.op-tier input').forEach(input => { input.checked = Number(input.value) === this.count; });
          return;
        }
      }
      const existing = cartPicks(snapshot);
      const desired = Math.max(existing.length, config.defaultTwo ? 2 : 1);
      this.count = desired;
      this.cartCount = existing.length;
      this.cartSignature = cartSignature(snapshot);
      this.picks = existing.slice(0, desired);
      if (!existing.length && this.seed && this.seed.available && !this.seed.requiresSellingPlan) {
        this.picks.push({ product: this.seed, variant: null });
      }
      this.querySelectorAll('.op-tier input').forEach(input => { input.checked = Number(input.value) === desired; });
    }
    openSlot(index) { this.picker.open(index, this.count, this.picks[index]?.product || (index === this.currentProductIndex ? this.seed : null), qs(this, `[data-op-slot="${index}"]`)); }
    clearSlot(index) {
      if (this.busy) return;
      this.picks[index] = null;
      this.dirty = true;
      storeDraft(this);
      this.render();
      qs(this, `[data-op-slot="${index}"]`)?.focus({ preventScroll: true });
    }
    render() {
      if (!state) return;
      const ready = offerValid && Boolean(config.collection) && compatible(state);
      qs(this, '[data-op-builder]').hidden = !ready;
      qs(this, '[data-op-fallback]').hidden = ready;
      if (!ready) return;
      qs(this, '[data-op-status]').textContent = config.previewOnly ? 'DRAFT PREVIEW · Proposed prices. Offer checkout is not enabled yet.' : '';
      const existing = eligibleCount(state);
      qs(this, '[data-op-price-context]').textContent = existing ? 'Editing your cart · changes apply when you save.' : this.dirty ? 'Selections saved · not added to cart' : '';
      const usShipping = config.shippingEnabled && config.shippingLive && amountValid(config.shippingThreshold)
        && config.currency === text('currency').trim().toUpperCase()
        && text('shipping_countries').toUpperCase().split(',').map(value => value.trim()).includes('US');
      this.querySelectorAll('.op-tier').forEach(label => {
        const count = Number(qs(label, 'input').value);
        const price = rotationTotal(count);
        const compare = ({ 1: 4999, 2: 8999, 3: 12999 })[count] || count * config.anchor;
        qs(label, 'input').disabled = this.busy;
        qs(label, '[data-op-tier-price]').textContent = money(price);
        qs(label, '[data-op-compare]').textContent = compare > price ? money(compare) : '';
        qs(label, '[data-op-save]').textContent = ({ 2: 'Save 20%', 3: 'Save 30%' })[count] || '';
        const shipping=qs(label,'[data-op-tier-shipping]');
        if (shipping) { shipping.hidden = !usShipping; shipping.textContent = price >= config.shippingThreshold ? 'Free US shipping' : `+ ${money(config.singleShipping)} US shipping`; }
      });
      const fragment = document.createDocumentFragment();
      let complete = 0;
      for (let index = 0; index < this.count; index++) {
        const pick = this.picks[index];
        if (pick?.variant) complete++;
        const item = el('li', `op-slot${pick?.variant ? ' op-slot--complete' : ''}${pick?.cartRef ? ' op-slot--cart' : ''}`);
        const button = el('button', 'op-slot__button'); button.type = 'button'; button.dataset.opSlot = index;
        button.append(el('span', 'op-slot__number', pick?.variant ? '✓' : index + 1));
        if (pick?.product.image) { const image = el('img'); image.src = pick.product.image; image.alt = ''; image.width = 56; image.height = 66; button.append(image); }
        else button.append(el('span', 'op-slot__placeholder', '+'));
        const copy = el('span', 'op-slot__copy');
        copy.append(el('strong', '', pick?.product.title || `Choose tee ${index + 1}`));
        const status = pick?.cartRef ? (String(pick.variant?.id) === pick.cartRef.variantId ? 'In cart' : 'Change ready') : 'Selected';
        copy.append(el('span', '', pick?.variant ? (pick.variant.title === 'Default Title' ? status : `${pick.variant.title} · ${status}`) : pick?.product ? 'Choose size' : 'Choose a design + size'));
        button.append(copy, el('span', 'op-slot__action', pick?.cartRef ? 'Swap' : pick?.variant ? 'Edit' : 'Choose →'));
        button.setAttribute('aria-label', `Tee ${index + 1}: ${pick?.product.title || 'choose design'}${pick?.variant ? `, ${pick.variant.title}, ${pick.cartRef ? 'swap item in cart' : 'edit'}` : ', choose size'}`);
        button.addEventListener('click', () => this.openSlot(index));
        item.append(button);
        if (pick?.product) {
          const remove = el('button', 'op-slot__remove', '×');
          remove.type = 'button';
          remove.setAttribute('aria-label', `Remove ${pick.product.title} from slot ${index + 1}`);
          remove.addEventListener('click', () => this.clearSlot(index));
          item.classList.add('op-slot--removable');
          item.append(remove, productLink(pick.product, this, index));
        }
        fragment.append(item);
      }
      const focusedSlot = document.activeElement?.dataset.opSlot;
      qs(this, '[data-op-slots]').replaceChildren(fragment);
      if (focusedSlot != null) qs(this, `[data-op-slot="${focusedSlot}"]`)?.focus({ preventScroll: true });
      this.renderCurrentProduct();
      qs(this, '[data-op-picked-count]').textContent = `${complete} / ${this.count} chosen`;
      const track=qs(this,'[data-op-completion]');
      if (track) track.replaceChildren(...Array.from({length:this.count},(_,i)=>el('span',this.picks[i]?.variant?'is-complete':'',this.picks[i]?.variant?'✓':i+1)));
      this.completedCount = complete;
      const currentTeesTotal = eligibleItems(state).reduce((sum, item) => sum + item.final_line_price, 0);
      this.projectedIncrement = rotationTotal(complete) - currentTeesTotal;
      this.querySelectorAll('op-shipping').forEach(bar=>bar.render?.(state));
      qs(this, '[data-op-total]').textContent = money(rotationTotal(this.count));
      const delivery = qs(this, '[data-op-delivery-note]');
      if (delivery) delivery.textContent = usShipping
        ? (rotationTotal(this.count) >= config.shippingThreshold ? 'Free US shipping. Taxes calculated at checkout.' : money(config.singleShipping) + ' US shipping. Taxes calculated at checkout.')
        : 'Shipping and taxes are calculated at checkout.';
      qs(this, '[data-op-buybar-caption]').textContent = existing ? `${existing} in cart · ${complete === this.count ? 'Ready' : `${this.count - complete} left`}` : `${this.count} ${this.count === 1 ? 'tee' : 'tees'} · ${complete === this.count ? 'Ready to add' : `${this.count - complete} left to choose`}`;
      const button = qs(this, '[data-op-submit]');
      const firstMissing = Array.from({ length: this.count }, (_, i) => i).find(i => !this.picks[i]?.variant);
      const changed = this.count !== existing || this.picks.slice(0, this.count).some(pick => !pick?.cartRef || String(pick.variant?.id) !== pick.cartRef.variantId);
      button.textContent = this.busy ? 'Saving your rotation…' : firstMissing == null ? (changed ? `${existing ? 'Update cart' : 'Add to cart'} — ${money(rotationTotal(this.count))}` : 'Rotation is in your cart') : (this.picks[firstMissing]?.product ? `Choose size for tee ${firstMissing + 1}` : `Choose tee ${firstMissing + 1}`);
      if (config.previewOnly && firstMissing == null) button.textContent = 'Rotation ready · Draft preview';
      button.disabled = this.busy || uncertain || (!changed && firstMissing == null) || (config.previewOnly && firstMissing == null);
      this.querySelectorAll('.op-slot__button, .op-slot__remove').forEach(node => { node.disabled = this.busy; });
    }
    renderCurrentProduct() {
      let panel = qs(this, '[data-op-current-product]');
      if (!panel) { panel = el('div', 'op-current-product'); panel.dataset.opCurrentProduct = ''; qs(this, '[data-op-slots]').before(panel); }
      const requested = Number(new URL(location.href).searchParams.get('op_slot')) - 1;
      const explicit = Number.isInteger(requested) && requested >= 0 && requested < this.count;
      const already = this.picks.slice(0,this.count).some(pick => pick?.product.handle === this.seed?.handle);
      this.currentProductIndex = null;
      panel.hidden = !this.seed?.available || (explicit ? this.picks[requested]?.product.handle === this.seed.handle : already) || this.busy;
      if (panel.hidden) return;
      const empty = Array.from({length:this.count},(_,i)=>i).find(i=>!this.picks[i]?.product);
      const target = explicit ? requested : empty != null ? empty : this.count < 3 ? this.count : null;
      this.currentProductIndex = target;
      const copy = el('div','op-current-product__copy');
      copy.append(el('span','', 'THIS TEE'),el('strong','',this.seed.title));
      const choose = el('button','op-button op-button--outline',target == null ? 'Replace tee' : this.picks[target]?.product ? 'Use in tee '+(target+1) : 'Choose as tee '+(target+1));
      choose.type='button';
      let slots;
      if (target == null) {
        slots=el('select','op-current-product__slot'); slots.setAttribute('aria-label','Tee to replace');
        for(let i=0;i<this.count;i++){const option=el('option','', 'Replace tee '+(i+1));option.value=i;slots.append(option);}
      }
      choose.addEventListener('click',()=>{
        const index=target == null ? Number(slots.value) : target;
        if(index >= this.count){this.count=index+1;this.dirty=true;storeDraft(this);this.querySelectorAll('.op-tier input').forEach(input=>{input.checked=Number(input.value)===this.count;});this.render();}
        this.picker.open(index,this.count,this.seed,choose);
      });
      panel.replaceChildren(copy,...(slots?[slots]:[]),choose);
    }
    async submit() {
      if (this.busy) return;
      const missing = Array.from({ length: this.count }, (_, i) => i).find(i => !this.picks[i]?.variant);
      if (missing != null) { this.openSlot(missing); return; }
      this.busy = true;
      qs(this, '[data-op-error]').textContent = '';
      this.render();
      try { await saveRotation(this); }
      catch (error) {
        qs(this, '[data-op-error]').textContent = error.message;
        qs(this, '[data-op-review]').hidden = !uncertain;
      } finally { this.busy = false; this.render(); }
    }
  }
  class Shipping extends HTMLElement {
    connectedCallback() {
      if (this.unsubscribe) return;
      this.unsubscribe = subscribers(snapshot => this.render(snapshot));
      if (!state) scheduleRefresh();
    }
    render(snapshot) {
      const rotation=this.closest('op-rotation');
      const projected=rotation && offerValid && compatible(snapshot);
      this.hidden = !shippingAvailable(snapshot);
      if(this.hidden)return;
      const amount=snapshot.cart.total_price + (projected ? rotation.projectedIncrement || 0 : 0);
      const remaining=Math.max(0,config.shippingThreshold-amount);
      const prefix=config.previewOnly?'Draft · ':'';
      qs(this,'[data-op-shipping-caption]').textContent = 'FREE US SHIPPING';
      qs(this,'[data-op-shipping-text]').textContent = prefix + (remaining ? text('shipping_away',{amount:money(remaining)}) : projected ? 'Free US shipping unlocked' : text('shipping_unlocked'));
      qs(this,'[data-op-progress]').value=Math.min(100,Math.max(0,amount/config.shippingThreshold*100));
      const note=qs(this,'small'); if(note)note.textContent=projected?'Updates as you choose.':text('shipping_scope');
      this.classList.toggle('is-unlocked',!remaining);
    }
    disconnectedCallback() { this.unsubscribe?.(); this.unsubscribe = null; }
  }
  class Bump extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true; this.busy = false; this.quickProducts = null;
      this.abort = new AbortController();
      this.picker = new Picker(this, pick => this.add(pick));
      qs(this, '[data-op-bump-open]').addEventListener('click', async event => {
        try {
          await refreshCart();
          const count = eligibleCount(state);
          if (count >= 1 && count < 3 && compatible(state)) this.picker.open(0, 1, null, event.currentTarget);
        } catch (error) { qs(this, '[data-op-error]').textContent = error.message; }
      }, { signal: this.abort.signal });
      this.unsubscribe = subscribers(snapshot => this.render(snapshot));
      if (!state) scheduleRefresh();
    }
    disconnectedCallback() { this.unsubscribe?.(); this.abort?.abort(); this.picker?.destroy(); this.initialized = false; }
    async loadQuick() {
      if (this.quickProducts || this.quickLoading || !config.collection) return;
      this.quickLoading = true;
      try {
        this.quickProducts = await loadRecommendationCatalog();
        this.renderQuick();
      } catch (_) { /* The See all button remains available. */ }
      finally { this.quickLoading = false; }
    }
    renderQuick() {
      if (!this.quickProducts || !state || !this.expectedQuote) return;
      const products = rankRecommendations(this.quickProducts, state).slice(0, 12);
      const region = qs(this, '[data-op-bump-quick]');
      region.hidden = !products.length;
      const fragment = document.createDocumentFragment();
      products.forEach(product => {
        const button = el('button', 'op-bump-product'); button.type = 'button';
        button.setAttribute('aria-label', `Choose size for ${product.title} — ${money(this.expectedQuote)}`);
        if (product.image) { const image = el('img'); image.src = product.image; image.alt = ''; image.width = 108; image.height = 132; button.append(image); }
        else button.append(el('span', 'op-image-placeholder', '+'));
        const copy = el('span', 'op-bump-product__copy');
        copy.append(el('strong', '', product.title));
        const price = el('span', 'op-bump-product__price');
        const was = el('s', '', money(config.anchor));
        const now = el('b', '', money(this.expectedQuote));
        price.append(was, now); copy.append(price); button.append(copy);
        button.addEventListener('click', () => this.picker.open(0, 1, product, button));
        const card=el('div','op-bump-product-card'); card.append(button,productLink(product,this));
        fragment.append(card);
      });
      qs(this, '[data-op-bump-products]').replaceChildren(fragment);
    }
    render(snapshot = state) {
      if (!snapshot) return;
      const count = eligibleCount(snapshot);
      const available = offerValid && compatible(snapshot) && count > 0 && !uncertain;
      const canAdd = available && count < 3;
      this.hidden = !available && !this.busy && !qs(this, '[data-op-error]').textContent;
      if (!available) return;

      qs(this, '[data-op-bump-offer]').hidden = !canAdd;
      if (!canAdd) return;

      const nextCount = count + 1;
      const targetTotal = rotationTotal(nextCount);
      const nextPrice = targetTotal - rotationTotal(count);
      this.expectedQuote = nextPrice;
      qs(this, '[data-op-bump-price]').textContent = money(nextPrice);
      qs(this, '[data-op-bump-normal]').textContent = money(config.anchor);
      qs(this, '[data-op-bump-discount]').textContent = money(Math.max(0, config.anchor - nextPrice));
      qs(this, '[data-op-bump-open]').textContent = `SEE ALL T-SHIRTS — ${money(nextPrice)}`;
      this.renderQuick();
      this.loadQuick();
    }
    async add(pick) {
      if (this.busy) return;
      this.busy = true;
      const confirm = qs(this, '[data-op-confirm]'); confirm.disabled = true; confirm.textContent = 'Adding your tee…';
      try { await addSelection([pick], this.expectedQuote, true); }
      catch (error) {
        qs(this, '[data-op-dialog-error]').textContent = error.message;
        qs(this, '[data-op-error]').textContent = error.message;
        qs(this, '[data-op-review]').hidden = !uncertain;
        if (uncertain) this.picker.close();
      } finally { this.busy = false; confirm.disabled = uncertain; confirm.textContent = text('use_tee'); this.render(); }
    }
  }
  // Keep unfinished selections visible while browsing, without duplicating PDP/cart controls.
  function draftDockPage(pathname = location.pathname) {
    const path = pathname.slice(root.length - 1);
    return path === '/' || /^\/(?:collections|search)(?:\/|$)/.test(path);
  }
  function hasPendingRotation(draft, snapshot) {
    if (!draft || !draft.picks.some(pick => pick?.variant)) return false;
    return draft.count !== eligibleCount(snapshot) || draft.picks.slice(0, draft.count)
      .some(pick => !pick?.cartRef || String(pick.variant?.id) !== pick.cartRef.variantId);
  }
  class DraftDock {
    constructor() {
      this.busy = false;
      this.abort = new AbortController();
      this.host = el('aside', 'op op-draft-dock');
      this.host.hidden = true;
      this.host.setAttribute('aria-label', 'Your saved rotation');
      this.bar = el('div', 'op-draft-dock__bar');
      this.summary = el('button', 'op-draft-dock__summary');
      this.summary.type = 'button';
      this.summary.setAttribute('aria-haspopup', 'dialog');
      this.summary.setAttribute('aria-controls', 'op-draft-sheet');
      this.action = el('button', 'op-button op-draft-dock__action', 'View');
      this.action.type = 'button';
      this.action.addEventListener('click', () => this.complete ? this.submit() : this.open());
      this.summary.addEventListener('click', () => this.open());
      this.bar.append(this.summary, this.action);
      this.dialog = el('dialog', 'op op-dialog op-draft-sheet');
      this.dialog.id = 'op-draft-sheet';
      this.dialog.setAttribute('aria-labelledby', 'op-draft-title');
      const head = el('div', 'op-dialog__head');
      const title = el('h2', '', 'Your rotation'); title.id = 'op-draft-title';
      this.closeButton = el('button', 'op-icon-button', '×');
      this.closeButton.type = 'button'; this.closeButton.setAttribute('aria-label', 'Close your rotation');
      this.closeButton.addEventListener('click', () => this.close());
      head.append(title, this.closeButton);
      const body = el('div', 'op-dialog__body');
      this.note = el('p', 'op-draft-sheet__note');
      this.rows = el('ol', 'op-draft-sheet__rows');
      this.error = el('p', 'op-error'); this.error.setAttribute('role', 'alert');
      this.review = el('a', 'op-text-button', 'Review your cart'); this.review.href = config.cartUrl; this.review.hidden = true;
      body.append(this.note, this.rows, this.error, this.review);
      const footer = el('div', 'op-dialog__footer');
      this.submitButton = el('button', 'op-button'); this.submitButton.type = 'button';
      this.submitButton.addEventListener('click', () => this.complete ? this.submit() : this.close());
      footer.append(this.submitButton);
      this.dialog.append(head, body, footer);
      this.dialog.addEventListener('close', () => {
        if (!document.querySelector('.op-dialog[open]')) document.documentElement.classList.remove('op-modal-open');
        if (!this.host.hidden) this.summary.focus({ preventScroll: true });
      });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      this.host.append(this.bar, this.dialog);
      this.host.addEventListener('click', event => { if (this.busy && event.target.closest('a')) event.preventDefault(); }, true);
      document.body.append(this.host);
      this.unsubscribe = subscribers(snapshot => { if (!this.busy) this.hydrate(snapshot); });
      window.addEventListener('storage', event => {
        if ((event.key === draftKey || event.key === null) && !this.busy) scheduleRefresh();
      }, { signal: this.abort.signal });
      refreshCart().catch(() => { this.host.hidden = true; });
    }
    hydrate(snapshot) {
      const draft = offerValid && compatible(snapshot) ? readDraft(snapshot) : null;
      this.draft = hasPendingRotation(draft, snapshot) ? draft : null;
      this.picks = this.draft?.picks || [];
      this.count = this.draft?.count || 2;
      this.cartSignature = this.draft?.signature || cartSignature(snapshot);
      this.existing = eligibleCount(snapshot);
      this.render();
    }
    productURL(pick, index) {
      const url = new URL(root + 'products/' + encodeURIComponent(pick.product.handle), location.origin);
      url.searchParams.set('op_slot', String(index + 1));
      return url.href;
    }
    close() { if (this.dialog.open) this.dialog.close(); }
    open() {
      if (this.host.hidden) return;
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('op-modal-open');
      this.closeButton.focus({ preventScroll: true });
    }
    render() {
      const visible = Boolean(this.draft);
      if (!visible) this.close();
      this.host.hidden = !visible;
      document.body.classList.toggle('op-has-draft-dock', visible);
      if (!visible) return;
      const selected = this.picks.filter(pick => pick?.variant).length;
      this.complete = Array.from({ length: this.count }, (_, index) => this.picks[index]).every(pick => pick?.variant);
      const preview = this.picks.find(pick => pick?.product.image);
      const summary = document.createDocumentFragment();
      if (preview) {
        const image = el('img'); image.src = preview.product.image; image.alt = ''; image.width = 36; image.height = 44;
        summary.append(image);
      }
      const copy = el('span');
      copy.append(el('strong', '', 'Your rotation'), el('span', '', `${selected}/${this.count} selected`));
      summary.append(copy); this.summary.replaceChildren(summary);
      this.summary.setAttribute('aria-label', `View your rotation: ${selected} of ${this.count} tees selected`);
      const addLabel = `${this.existing ? 'Update cart' : 'Add to cart'} — ${money(rotationTotal(this.count))}`;
      this.action.textContent = this.busy ? 'Saving…' : this.complete ? addLabel : 'View';
      this.action.disabled = this.busy || uncertain;
      this.submitButton.textContent = this.busy ? 'Saving…' : this.complete ? addLabel : 'Keep browsing';
      this.submitButton.disabled = this.busy || (this.complete && uncertain);
      this.note.textContent = this.existing ? 'Your changes will apply when you update the cart.' : 'Not added to cart yet.';
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < this.count; index++) {
        const pick = this.picks[index];
        const row = el('li', 'op-draft-row');
        if (pick?.product) {
          const link = el('a', 'op-draft-row__product'); link.href = this.productURL(pick, index);
          if (pick.product.image) { const image = el('img'); image.src = pick.product.image; image.alt = ''; image.width = 48; image.height = 60; link.append(image); }
          const details = el('span');
          details.append(el('strong', '', pick.product.title), el('span', '', pick.variant ? `Size ${pick.variant.title}` : 'Choose size'), el('u', '', 'View / change'));
          link.append(details);
          const remove = el('button', 'op-draft-row__remove', '×'); remove.type = 'button';
          remove.setAttribute('aria-label', `Remove tee ${index + 1}: ${pick.product.title}`); remove.disabled = this.busy;
          remove.addEventListener('click', () => {
            if (this.busy) return;
            this.picks[index] = null;
            storeDraft(this);
            this.hydrate(state);
            if (this.dialog.open) this.closeButton.focus({ preventScroll: true });
          });
          row.append(link, remove);
        } else {
          const link = el('a', 'op-draft-row__empty', `+ Choose tee ${index + 1}`);
          link.href = root + 'collections/' + encodeURIComponent(config.collection);
          row.append(link);
        }
        fragment.append(row);
      }
      this.rows.replaceChildren(fragment);
      this.review.hidden = !uncertain;
    }
    async submit() {
      if (this.busy || !this.complete || uncertain) return;
      this.busy = true; this.error.textContent = ''; this.render();
      try { await saveRotation(this); }
      catch (error) {
        this.error.textContent = error.message;
        this.review.hidden = !uncertain;
        this.open();
      } finally { this.busy = false; this.render(); }
    }
  }
  if (offerValid && config.collection && draftDockPage() && !document.querySelector('op-rotation')) new DraftDock();

  syncProductChart();
  document.addEventListener('shopify:section:load',syncProductChart);
  window.OverplayedRotation = Object.freeze({ refreshCart, quote, version: '1.5.0' });
  customElements.define('op-rotation', Rotation);
  customElements.define('op-shipping', Shipping);
  customElements.define('op-bump', Bump);
})();
